import React, { useEffect } from "react";
import RegisterHeader from "./Common/Header";
import RegisterSteps from "./Common/Steps";
import Typography from "@mui/material/Typography";
import {
  Button,
  Container,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Box,
  Card,
} from "@mui/material";
import "./Register.scss";
import { useNavigate } from "react-router-dom";
import { useRegister } from "../../context/RegisterContext";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Address } from "../../types/RegisterType";
import { REQUIRED_FIELD } from "../../utils/constants";
import CepMask from "../InputMask/CepMask";
import Footer from "../Footer";
import ReactGA from "react-ga4";

const RegisterAddress = () => {
  const navigate = useNavigate();
  const { registerAddress, getRegisterData, canProceed } = useRegister();
  
  const steps = [
    'Endereço',
    'Responsável',
    'Negócio',
    'Explicação',
    'Categoria',
    'Login',
    'Confirmação'
  ];

  const schemaAddress = yup.object({
    zip_code: yup.string().required(REQUIRED_FIELD),
    state: yup.string().required(REQUIRED_FIELD),
    city: yup.string().required(REQUIRED_FIELD),
    neighborhood: yup.string().required(REQUIRED_FIELD),
    street: yup.string().required(REQUIRED_FIELD),
    number: yup.string().required(REQUIRED_FIELD),
    complement: yup.string().optional(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schemaAddress),
  });

  const onSubmitAddress = (data: Address) => {
    registerAddress(data);
    navigate("/register/owner");
  };

  const fetchAddress = async (cep: string) => {
    if (cep.length === 8) {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setValue("state", data.uf);
        setValue("city", data.localidade);
        setValue("neighborhood", data.bairro);
        setValue("street", data.logradouro);
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!canProceed()) {
      navigate("/parceiros");
      return;
    }

    const { address } = getRegisterData();
    setValue("zip_code", address?.zip_code as string);
    setValue("state", address?.state as string);
    setValue("city", address?.city as string);
    setValue("neighborhood", address?.neighborhood as string);
    setValue("street", address?.street as string);
    setValue("number", address?.number as string);
    setValue("complement", address?.complement as string);
  }, []);

  return (
    <div className="Register" style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
    }}>
      <RegisterHeader />

      <Container maxWidth="md" className="MainContainer">
        <RegisterSteps currentStep={1} steps={steps} />
        
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h1" sx={{ 
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 'bold',
            color: '#0f172a',
            marginBottom: 2
          }}>
            Endereço do lava-rápido
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Preencha as informações de endereço do seu lava-rápido
          </Typography>
        </Box>

        <Card sx={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          padding: 3,
          borderRadius: 3,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          backgroundColor: 'white'
        }}>
          <form onSubmit={handleSubmit(onSubmitAddress)}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="zip_code"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="zip_code">CEP</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="zip_code"
                        type="text"
                        placeholder="00000-000"
                        fullWidth
                        error={!!errors.zip_code}
                        inputComponent={CepMask as any}
                        onBlur={() => fetchAddress(field.value)}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.zip_code?.message}
                      </FormHelperText>
                    </>
                  )}
                />
                <FormHelperText>
                  <a
                    href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                    target="_blank"
                    rel="noreferrer"
                    className="Link"
                  >
                    Descubra o CEP
                  </a>
                </FormHelperText>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Controller
                  name="state"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="estado">Estado</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="estado"
                        type="text"
                        placeholder="UF"
                        fullWidth
                        error={
                          control.getFieldState("state").isTouched &&
                          !!errors.state
                        }
                        readOnly
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.state?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Controller
                  name="city"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="city">Cidade</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="city"
                        type="text"
                        placeholder="Cidade do seu negócio"
                        fullWidth
                        error={!!errors.city}
                        readOnly
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.city?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="neighborhood"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="neighborhood">Bairro</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="neighborhood"
                        type="text"
                        placeholder="Exemplo: Centro"
                        fullWidth
                        error={!!errors.neighborhood}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.neighborhood?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="street"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="street">Endereço</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="street"
                        type="text"
                        placeholder="Exemplo: Avenida Brasil"
                        fullWidth
                        error={!!errors.street}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.street?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Controller
                  name="number"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="number">Número</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="number"
                        type="text"
                        placeholder="Exemplo: 123"
                        fullWidth
                        error={!!errors.number}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.number?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Controller
                  name="complement"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="complement">Complemento</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="complement"
                        type="text"
                        placeholder="Exemplo: Sala 1"
                        fullWidth
                        error={!!errors.complement}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.complement?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate("/parceiros")}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderColor: '#d1d5db',
                    color: '#374151',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                      borderColor: '#9ca3af',
                    },
                  }}
                >
                  Voltar
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  onClick={() => {
                    ReactGA.event("continue_click", {
                      event: 'continue_click',
                      button_id: 'continue',
                      page_path: window.location.pathname
                    })
                  }}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Card>
        
        <Box sx={{ textAlign: 'center', marginTop: 4, marginBottom: 2 }}>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: '1rem'
          }}>
            Ficou alguma dúvida?{" "}
            <a 
              href="/fale-conosco" 
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Fale Conosco
            </a>
          </Typography>
        </Box>
      </Container>

      <Footer/>
    </div>
  );
};

export default RegisterAddress;
